import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Navbar from "../Navbar";
import TypePicker from "./TypePicker";
import TypeForm from "./TypeForm";
import { SMC_FORM_SCHEMA } from "./smcSchema";
import ApiService from "../../helpers/Services";
import "./createSmc.css";

const initialFormData = (typeKey) => {
  const schema = SMC_FORM_SCHEMA[typeKey];
  const data = {};
  for (const f of schema.fields) {
    if (f.default !== undefined) data[f.name] = f.default;
    else if (f.type === "tags") data[f.name] = [];
    else if (f.type === "toggle") data[f.name] = false;
  }
  return data;
};

const CreateSmcTicket = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const schema = useMemo(
    () => (selectedType ? SMC_FORM_SCHEMA[selectedType] : null),
    [selectedType],
  );

  const handleSelectType = (typeKey) => {
    setSelectedType(typeKey);
    setFormData(initialFormData(typeKey));
    setErrors({});
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
    setErrors({});
  };

  const handleFieldChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const errs = {};
    for (const f of schema.fields) {
      if (!f.required) continue;
      const v = formData[f.name];
      if (
        v === undefined ||
        v === null ||
        v === "" ||
        (Array.isArray(v) && v.length === 0) ||
        (f.type === "file" && !v?.url)
      ) {
        errs[f.name] = "Required";
      }
    }
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    const payload = { requestType: selectedType };
    for (const f of schema.fields) {
      const v = formData[f.name];
      if (v === undefined || v === null || v === "") continue;
      if (f.type === "file") payload[f.name] = v.url;
      else payload[f.name] = v;
    }
    try {
      const resp = await ApiService.post("create-smc-ticket", payload);
      toast.success(`Ticket #${resp.ticketId} created`);
      navigate(`/manage-ticket/ticket/view/${resp.workflowId}`);
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.error || e?.message || "Create failed";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Navbar />
      <div className="smc-create-page">
        <div className="smc-create-header">
          {step === 2 ? (
            <button type="button" className="smc-back-btn" onClick={handleBack}>
              ← Back to type picker
            </button>
          ) : (
            <button type="button" className="smc-back-btn" onClick={() => navigate("/manage-ticket")}>
              ← Back to tickets
            </button>
          )}
          <div style={{ fontSize: "0.85rem", color: "#6b7e8f" }}>
            Step {step} of 2
          </div>
        </div>

        {step === 1 && <TypePicker onSelect={handleSelectType} />}
        {step === 2 && schema && (
          <>
            <TypeForm
              typeKey={selectedType}
              formData={formData}
              onChange={handleFieldChange}
              errors={errors}
            />
            <div className="smc-form" style={{ paddingBottom: "2rem" }}>
              <div className="smc-form-actions">
                <button type="button" className="smc-btn-secondary" onClick={handleBack}>
                  Back
                </button>
                <button
                  type="button"
                  className="smc-btn-primary"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? "Creating…" : "Create Ticket"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CreateSmcTicket;
